import { useRef, useState, useEffect, useLayoutEffect, useCallback } from 'react';
import type { ChangeEvent, MouseEvent, KeyboardEvent as ReactKeyboardEvent } from 'react';
import type { Editor as TiptapEditor } from '@tiptap/core';
import styled from 'styled-components';

import {
  Button,
  CardContent,
  Grid,
  Icon,
  registerIcon,
  Input,
  RadioButton,
  RadioButtonGroup,
  useOuterEvent,
  Form,
  useI18n,
  Text,
  useElement,
  useEscape,
  TouchDialog,
  isTouchDevice,
  defaultThemeProp
} from '@pega/cosmos-react-core';
import type { ForwardProps } from '@pega/cosmos-react-core';
import * as chainIcon from '@pega/cosmos-react-core/lib/components/Icon/icons/chain.icon';

import ToolbarButton from '../../RichTextEditor/Toolbar/ToolbarButton';
import { getKeyCommand } from '../../RichTextEditor/Toolbar/utils';
import type { LinkCustomSourceConfig } from '../Editor.types';
import { StyledEditPopover } from '../Editor.styles';
import useCloseOnEditorClick from '../hooks/useCloseOnEditorClick';

registerIcon(chainIcon);

const StyledTouchDialogContent = styled(CardContent)(
  ({ theme }) => `
  flex: 1;

  > div {
    display: flex;
    flex-direction: column;
    min-height: 100%;
  }

  > div > div:not([aria-hidden='true']):nth-last-child(2) {
    margin-top: auto;
    padding-block-end: ${theme.base.spacing};
  }
`
);

StyledTouchDialogContent.defaultProps = defaultThemeProp;

interface AnchorButtonProps {
  osx: boolean;
  editor: TiptapEditor;
  linkCustomSource?: LinkCustomSourceConfig;
}

const AnchorButton = ({
  osx,
  editor,
  linkCustomSource,
  ...restProps
}: AnchorButtonProps & ForwardProps) => {
  const t = useI18n();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const touchDialogRef = useRef<HTMLDivElement>(null);
  const [textInputEl, setTextInputEl] = useElement<HTMLInputElement>();
  const [urlInputEl, setUrlInputEl] = useElement<HTMLInputElement>();
  const [popoverEl, setPopoverEl] = useElement<HTMLDivElement>();
  const [linkText, setLinkText] = useState('');
  const [url, setUrl] = useState('');
  const [urlMatch, setUrlMatch] = useState(true);
  const [anchorMenu, setAnchorMenu] = useState(false);
  const [shouldFocusInput, setShouldFocusInput] = useState(false);
  const [linkSourceType, setLinkSourceType] = useState<'url' | 'field'>('url');
  const hasFieldLinkMode = Boolean(linkCustomSource?.picker && linkCustomSource?.onSubmit);
  const canSubmitCustomSource = linkCustomSource?.canSubmit ?? true;
  const wasAnchorMenuVisibleRef = useRef(false);
  // Store the original selection range when menu opens so we can restore it when creating the link
  const originalSelectionRef = useRef<{ from: number; to: number } | null>(null);
  // Track whether the popover was opened via linkCustomSource.edit
  const openedViaEditCustomSourceRef = useRef(false);
  // Track whether popover was initialized from an existing field link (selection-based edit path)
  const initializedFromExistingFieldLinkRef = useRef(false);
  const tooltip = getKeyCommand(osx, ({ ctrl }) => `${t('rte_link')} (${ctrl}K)`);

  const parseCustomSourceFromHref = useCallback((href: string) => {
    const decodedHref = href
      .replaceAll('&quot;', '"')
      .replaceAll('&lt;', '<')
      .replaceAll('&gt;', '>')
      .replaceAll('&amp;', '&');
    if (!decodedHref.includes('<pega-reference')) {
      return null;
    }

    const idMatch = /data-rule-id=["']([^"']+)["']/i.exec(decodedHref);
    if (!idMatch) {
      return null;
    }

    const namespaceMatch = /data-rule-namespace=["']([^"']+)["']/i.exec(decodedHref);
    return {
      id: idMatch[1],
      namespace: namespaceMatch?.[1]
    };
  }, []);

  const openMenu = (opts: { focusInput?: boolean } = {}) => {
    // Save the original selection before opening the menu
    const { from, to } = editor.state.selection;
    originalSelectionRef.current = { from, to };
    initializedFromExistingFieldLinkRef.current = false;
    setAnchorMenu(true);
    if (opts.focusInput) {
      setShouldFocusInput(true);
    }
  };

  const resetMenu = useCallback(
    (dismissReason: 'cancel' | 'apply' = 'cancel') => {
      setLinkText('');
      setUrl('');
      setUrlMatch(true);
      setAnchorMenu(false);
      setLinkSourceType('url');
      originalSelectionRef.current = null;
      initializedFromExistingFieldLinkRef.current = false;
      if (openedViaEditCustomSourceRef.current) {
        openedViaEditCustomSourceRef.current = false;
        linkCustomSource?.onDismissEdit?.(dismissReason);
      }
    },
    [linkCustomSource]
  );

  useOuterEvent('mousedown', [popoverEl, buttonRef, touchDialogRef], () => {
    if (anchorMenu) {
      resetMenu();
    }
  });

  // Close menu when clicking inside the editor iframe
  const handleEditorClick = useCallback(() => {
    if (anchorMenu) {
      resetMenu();
    }
  }, [anchorMenu, resetMenu]);
  useCloseOnEditorClick(anchorMenu, handleEditorClick);

  const isValidUrl = (urlString: string): boolean => {
    if (!urlString.trim()) return false;
    const allowedProtocols = /^(https?|mailto|tel):/i;
    const hasProtocol = /^[a-z][a-z0-9.+-]*:/i.test(urlString);

    try {
      const testUrl = hasProtocol ? urlString : `https://${urlString}`;
      if (hasProtocol && !allowedProtocols.test(urlString)) {
        return false;
      }
      const urlObj = new URL(testUrl);
      return URL.canParse?.(testUrl) ?? Boolean(urlObj.href);
    } catch {
      return false;
    }
  };

  const createLink = useCallback(() => {
    if (!url || !isValidUrl(url)) {
      return;
    }

    // Only allow safe protocols to prevent XSS (e.g., javascript: URLs)
    const allowedProtocols = /^(https?|mailto|tel):/i;
    const hasProtocol = /^[a-z][a-z0-9.+-]*:/i.test(url);

    let normalizedUrl: string;
    if (hasProtocol) {
      // Reject URLs with disallowed protocols
      if (!allowedProtocols.test(url)) {
        return;
      }
      normalizedUrl = url;
    } else {
      normalizedUrl = `https://${url}`;
    }

    // Use the original selection to determine if text was selected in the editor
    const originalSelection = originalSelectionRef.current;

    if (originalSelection && originalSelection.from !== originalSelection.to) {
      // Text was originally selected - restore selection and apply link
      // Then collapse selection to end and clear stored mark so subsequent typing isn't linked
      editor
        .chain()
        .focus()
        .setTextSelection({ from: originalSelection.from, to: originalSelection.to })
        .setLink({ href: normalizedUrl })
        .setTextSelection(originalSelection.to)
        .unsetMark('link')
        .run();
    } else if (linkText) {
      // No text was originally selected, insert new link with the text entered in modal
      editor
        .chain()
        .focus()
        .insertContent({
          type: 'text',
          text: linkText,
          marks: [{ type: 'link', attrs: { href: normalizedUrl } }]
        })
        .unsetMark('link')
        .insertContent(' ')
        .run();
    }
    resetMenu('apply');
  }, [url, linkText, editor, resetMenu]);

  useEffect(() => {
    const isOpening = anchorMenu && !wasAnchorMenuVisibleRef.current;
    wasAnchorMenuVisibleRef.current = anchorMenu;

    if (isOpening && originalSelectionRef.current) {
      // Use the stored original selection to populate the modal
      // This avoids issues where focus shift may collapse the editor selection
      const { from, to } = originalSelectionRef.current;
      const text = editor.state.doc.textBetween(from, to);
      const linkMark = editor.getAttributes('link');

      if (linkMark.href) {
        // Check if href contains a pega-reference (escaped or unescaped)
        const isPegaReference =
          linkMark.href.includes('<pega-reference') || linkMark.href.includes('&lt;pega-reference');
        if (isPegaReference) {
          const customSourceValue = parseCustomSourceFromHref(linkMark.href);
          if (customSourceValue) {
            initializedFromExistingFieldLinkRef.current = true;
            linkCustomSource?.onEditSelectionChange?.({
              id: customSourceValue.id,
              text: text || '',
              namespace: customSourceValue.namespace
            });
          } else {
            initializedFromExistingFieldLinkRef.current = false;
            linkCustomSource?.onEditSelectionChange?.({ id: '', text: '', namespace: undefined });
          }
          // Don't populate URL field with pega-reference - leave it empty
          // The field picker will handle field links
          if (hasFieldLinkMode) {
            setLinkSourceType('field');
          }
          setLinkText(text || '');
        } else {
          initializedFromExistingFieldLinkRef.current = false;
          linkCustomSource?.onEditSelectionChange?.({ id: '', text: '', namespace: undefined });
          setLinkSourceType('url');
          setUrl(linkMark.href);
          setLinkText(text || '');
        }
      } else {
        initializedFromExistingFieldLinkRef.current = false;
        linkCustomSource?.onEditSelectionChange?.({ id: '', text: '', namespace: undefined });
        setLinkSourceType('url');
        setLinkText(text || '');
      }
    } else if (!anchorMenu) {
      resetMenu();
    }
  }, [
    anchorMenu,
    editor,
    hasFieldLinkMode,
    linkCustomSource,
    parseCustomSourceFromHref,
    resetMenu
  ]);

  useEffect(() => {
    if (linkCustomSource?.edit && hasFieldLinkMode) {
      openedViaEditCustomSourceRef.current = true;
      initializedFromExistingFieldLinkRef.current = true;
      setLinkText(linkCustomSource.edit.text);
      setLinkSourceType('field');
      setAnchorMenu(true);
    }
  }, [linkCustomSource, hasFieldLinkMode]);

  const preventDef = (e: MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const isLinkActive = () => {
    return editor.isActive('link') && editor.isFocused;
  };

  const cancelAnchorCreation = useCallback(
    (event?: KeyboardEvent | MouseEvent) => {
      if (
        ((event && 'key' in event && event.key === 'Escape') ||
          event?.type === 'click' ||
          !event) &&
        anchorMenu
      ) {
        if (event && 'key' in event && event.key === 'Escape') {
          event.stopPropagation();
        }
        const originalSelection = originalSelectionRef.current;

        resetMenu();
        if (originalSelection) {
          // Refocus the original active selection in the editor
          editor
            .chain()
            .focus()
            .setTextSelection({ from: originalSelection.from, to: originalSelection.to })
            .run();
        } else {
          buttonRef.current?.focus();
        }
      }
    },
    [anchorMenu, editor, resetMenu]
  );

  // Ctrl+K shortcut to open link dialog from within the editor iframe
  useEffect(() => {
    const handleCtrlK = (e: KeyboardEvent) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        openMenu({ focusInput: true });
      }
    };

    const iframeWindow = editor.view.dom.ownerDocument.defaultView;
    iframeWindow?.addEventListener('keydown', handleCtrlK);
    return () => iframeWindow?.removeEventListener('keydown', handleCtrlK);
  }, [editor]);

  // Escape to close popover - listen on both iframe and parent document
  // since focus may be in either location depending on how the popover was opened
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && anchorMenu) {
        e.preventDefault();
        cancelAnchorCreation();
      }
    };
    const iframeWindow = editor.view.dom.ownerDocument.defaultView;
    iframeWindow?.addEventListener('keydown', handleEscape);
    document.addEventListener('keydown', handleEscape);
    return () => {
      iframeWindow?.removeEventListener('keydown', handleEscape);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [editor, anchorMenu, cancelAnchorCreation]);

  useEscape(cancelAnchorCreation, popoverEl);

  useLayoutEffect(() => {
    if (shouldFocusInput) {
      const originalSelection = originalSelectionRef.current;
      const hasActiveSelection =
        originalSelection && originalSelection.from !== originalSelection.to;
      const inputToFocus = hasActiveSelection ? urlInputEl : textInputEl;
      if (inputToFocus) {
        inputToFocus.focus();
        setShouldFocusInput(false);
      }
    }
  }, [textInputEl, urlInputEl, shouldFocusInput]);

  useEffect(() => {
    // These events must be added here so they run before the native event in useArrows (used in the toolbar).
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') {
        e.stopPropagation();
      }
    };
    textInputEl?.addEventListener('keydown', onKeyDown);
    urlInputEl?.addEventListener('keydown', onKeyDown);
    return () => {
      textInputEl?.removeEventListener('keydown', onKeyDown);
      urlInputEl?.removeEventListener('keydown', onKeyDown);
    };
  }, [textInputEl, urlInputEl]);

  const linkFormContent = (
    <Form
      as='div'
      actions={
        <>
          <Button variant='secondary' onClick={cancelAnchorCreation} type='button'>
            {t('cancel')}
          </Button>
          <Button
            variant='primary'
            disabled={
              linkSourceType === 'url'
                ? !url.trim() || !urlMatch
                : !linkCustomSource?.onSubmit || !canSubmitCustomSource
            }
            onClick={(e: MouseEvent) => {
              e.preventDefault();
              if (linkSourceType === 'url') {
                createLink();
                return;
              }
              if (!linkCustomSource?.onSubmit) {
                return;
              }
              linkCustomSource.onSubmit(linkText);
              resetMenu('apply');
            }}
          >
            {t('apply')}
          </Button>
        </>
      }
    >
      <Grid container={{ rowGap: 2 }}>
        <Input
          label={t('rte_link_text')}
          value={linkText}
          onClick={preventDef}
          onChange={(e: ChangeEvent<HTMLInputElement>) => setLinkText(e.target.value)}
          ref={setTextInputEl}
        />
        {hasFieldLinkMode && (
          <RadioButtonGroup
            name='linkSourceType'
            label={t('rte_link_source')}
            onChange={e => {
              const { value } = e.target;
              if (value === 'url' || value === 'field') {
                setLinkSourceType(value);
                if (
                  value === 'field' &&
                  !openedViaEditCustomSourceRef.current &&
                  !initializedFromExistingFieldLinkRef.current
                ) {
                  linkCustomSource?.onEditSelectionChange?.({
                    id: '',
                    text: '',
                    namespace: undefined
                  });
                }
              }
            }}
            inline
          >
            <RadioButton label={t('rte_link_url')} value='url' checked={linkSourceType === 'url'} />
            <RadioButton
              label={t('rte_link_field')}
              value='field'
              checked={linkSourceType === 'field'}
            />
          </RadioButtonGroup>
        )}
        {linkSourceType === 'url' ? (
          <Input
            label={t('rte_link_url')}
            value={url}
            onClick={preventDef}
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              const urlInput = e.target.value;
              setUrl(urlInput);
              if (!urlMatch) {
                setUrlMatch(true);
              }
            }}
            onBlur={() => {
              setUrlMatch(!url || isValidUrl(url));
            }}
            info={url && !urlMatch ? t('rte_invalid_url') : ''}
            status={url && !urlMatch ? 'error' : undefined}
            ref={setUrlInputEl}
          />
        ) : (
          linkCustomSource?.picker
        )}
      </Grid>
    </Form>
  );

  return (
    <>
      <ToolbarButton
        ref={buttonRef}
        onMouseDown={e => {
          e.preventDefault();
          openMenu();
        }}
        onKeyDown={(e: ReactKeyboardEvent) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            openMenu({ focusInput: true });
          }
        }}
        active={isLinkActive()}
        tooltip={tooltip}
        label={t('rte_link')}
        {...restProps}
      >
        <Icon name='chain' />
      </ToolbarButton>
      {isTouchDevice ? (
        <TouchDialog
          ref={touchDialogRef}
          open={anchorMenu}
          heading={t('rte_add_link')}
          onDismiss={() => cancelAnchorCreation()}
        >
          <StyledTouchDialogContent>{linkFormContent}</StyledTouchDialogContent>
        </TouchDialog>
      ) : (
        <StyledEditPopover
          show={anchorMenu}
          target={buttonRef.current}
          ref={setPopoverEl}
          placement='bottom'
        >
          <CardContent>
            <Text variant='h2'>{t('rte_add_link')}</Text>
            {linkFormContent}
          </CardContent>
        </StyledEditPopover>
      )}
    </>
  );
};

export default AnchorButton;
